/**
 * API query wrappers with cache etc.
 *
 * We often deploy queries ahead of the rest of the feature, to resolve conflicts,
 * so we allow unused functions for now.
 *
 */

/* eslint-disable no-magic-numbers */

import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { apiCollections, apiCreators, apiNiches, apiSearch, apiTags, apiMain, apiVisitor } from "@/common/services";
import { isAuthenticated } from "@/common/services/Auth";
import {
  ICollection,
  ICreatorsItemsList,
  IErrorData,
  IFollowingListResponse,
  IGetRelatedNicheParams,
  IGifInfo,
  IGifList,
  INiche,
  INicheList,
  INichesItems,
  IShortUserList,
  ITag,
  ITagList,
  ITagSuggestedOptions,
  ITagsList,
  IUser,
  IUserSearchResults,
  ITagLandingFeed,
  IAnalyticsResponse,
} from "@/common/services/types";
import { IUserGifsParams, IPatchGifParams } from "@/common/services/RedGifsService";
import { IGetMatchedTagsParams } from "@/common/services/TagsService";
import { IUpdateParams } from "@/common/services/VisitorService";

const TRENDING_TAGS_KEY = "trending-tags";
const TRENDING_CREATORS_KEY = "trending-creators";
const TRENDING_NICHES_KEY = "trending-niches";
const SINGLE_NICHE_KEY = "single-niche";

const DEFAULT_OPTIONS = {
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  retry: false,
};

export const useFollowingQuery = () => {
  return useQuery({
    queryKey: ["following"],
    queryFn: async (): Promise<IFollowingListResponse|undefined> => {
      const auth = await isAuthenticated();

      if (auth) {
        return await apiMain.fetchUserFollows({
          count: 100,
        });
      } else {
        return undefined;
      }
    },
    ...DEFAULT_OPTIONS,
    gcTime: 0,
  });
};

/**
 * Returns a list of gif ids that the user liked previously.
 */
export const useLikesQuery = () => {
  return useQuery({
    queryKey: ["likes"],
    queryFn: async (): Promise<string[]> => {
      try {
        return await apiMain.getLikedGifs();
      } catch (e) {
        console.error("Error checking for user auth.", e);
        return [];
      }
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useTrendingTags = () => {
  return useQuery({
    queryKey: [TRENDING_TAGS_KEY],
    queryFn: async (): Promise<ITag[]> => {
      return await apiTags.getTrendingTags();
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useTrendingCreators = () => {
  return useQuery({
    queryKey: [TRENDING_CREATORS_KEY],
    queryFn: async (): Promise<IUser[]> => {
      return (await apiCreators.getTrendingCreators()).items;
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useTrendingNiches = (previews?: boolean) => {
  return useQuery({
    queryKey: [TRENDING_NICHES_KEY],
    queryFn: async (): Promise<INiche[]> => {
      const res = await apiNiches.getTrendingNiches(previews);
      return res.niches;
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useSuggestedNiches = () => {
  return useQuery({
    queryKey: ["personal-suggested-niches"],
    queryFn: async (): Promise<INiche[]> => {
      const auth = await isAuthenticated();

      if (auth) {
        return await apiNiches.getSuggestedNiches();
      } else {
        return [];
      }
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useSingleNicheQuery = (id?: string, enabled?: boolean) => {
  return useQuery<INiche | undefined, IErrorData>({
    queryKey: [SINGLE_NICHE_KEY, id],
    queryFn: async () => {
      if (id === undefined) {
        return undefined;
      }

      try {
        return await apiNiches.getNiche(id);
      } catch (e) {
        throw convertException(e);
      }
    },
    ...DEFAULT_OPTIONS,
    enabled,
  });
};

export const useUserInfo = (id: string|undefined, enabled: boolean) => {
  return useQuery<IUser, AxiosError<IErrorData>>({
    queryKey: ["user-info", id],
    queryFn: async () => {
      if (id === undefined) {
        throw Error("user id not specified");
      }

      return await apiMain.fetchCreatorById(id);
    },
    ...DEFAULT_OPTIONS,
    enabled,
  });
};

export const useFollowingNichesQuery = (props: {
  previews: boolean;
  gcTime?: number;
  refetchOnMount?: boolean;
}) => {
  return useQuery({
    queryKey: ["following-niches", props],
    queryFn: async (): Promise<INicheList> => {
      const auth = await isAuthenticated();

      if (auth) {
        console.debug("The user is authenticated, requesting following niches.");
        return await apiNiches.getFollowingNiches({
          previews: props.previews,
        });
      } else {
        console.debug("The user is not authenticated, not requesting following niches.");
        return { niches: [] };
      }
    },
    ...DEFAULT_OPTIONS,
    gcTime: props.gcTime,
    refetchOnMount: props.refetchOnMount || DEFAULT_OPTIONS.refetchOnMount,
  });
};

export const useNewVerifiedCreators = () => {
  return useQuery({
    queryKey: ["new-verified-creators"],
    queryFn: async (): Promise<IShortUserList> => {
      return await apiCreators.getNewVerifiedCreators();
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useSearchCreatorsQuery = (params: {
    order: string;
    page: number;
    tags?: string;
    verified?: boolean;
}) => {
  return useQuery({
    queryKey: ["search-creators", params],
    queryFn: async (): Promise<IUserSearchResults> => {
      return await apiCreators.searchCreators({
        order: params.order,
        page: params.page,
        tags: params.tags,
        verified: params.verified,
      });
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useGifQuery = (id: string, enabled: boolean) => {
  return useQuery<IGifInfo, AxiosError<IErrorData>>({
    queryKey: ["single-gif-info", id],
    queryFn: async (): Promise<IGifInfo> => {
      return await apiMain.fetchGifById(id);
    },
    ...DEFAULT_OPTIONS,
    enabled,
  });
};

export const useAccountQuery = (props: {
  userName: string;
}) => {
  return useQuery({
    queryKey: ["account-info", props],
    queryFn: async (): Promise<IUser> => {
      return await apiMain.getUser({
        userName: props.userName,
      });
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useVerifyUserProfileMutation = () => {
  return useMutation({
    mutationFn: (verifyData: { [key: string]: string }) => {
      return apiVisitor.verifyUserProfile(verifyData);
    }
  });
};

export const useCollectionQuery = (props: {
  userName: string;
  collectionId: string;
  showPrivate: boolean;
}) => {
  return useQuery({
    queryKey: ["collection-info", props],
    queryFn: async (): Promise<ICollection> => {
      if (props.showPrivate) {
        return await apiCollections.getMyCollection({
          collectionId: props.collectionId,
        });
      } else {
        return await apiCollections.getCollectionById({
          userName: props.userName,
          collectionId: props.collectionId,
        });
      }
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useMeQuery = () => {
  return useQuery({
    queryKey: ["get-me"],
    queryFn: async (): Promise<IUser|null> => {
      const auth = await isAuthenticated();

      if (auth) {
        return await apiMain.getMe();
      } else {
        return null;
      }
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useNichesQuery = (props: {
  order: string;
  sort?: string;
  previews?: boolean;
  tag?: string;
  page?: number;
  count?: number;
}) => {
  return useQuery({
    queryKey: ["all-niches", props],
    queryFn: async () => {
      return await apiNiches.getNiches({
        order: props.order,
        sort: props.sort,
        tag: props.tag,
        previews: props.previews ?? true,
        page: props.page,
        count: props.count,
      });
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useTopThisWeekQuery = () => {
  return useQuery({
    queryKey: ["top-this-week"],
    queryFn: async (): Promise<IGifList> => {
      return await apiSearch.getTopThisWeek();
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useTrendingGifsQuery = () => {
  return useQuery({
    queryKey: ["trending-gifs"],
    queryFn: async (): Promise<IGifList> => {
      return await apiSearch.getTrendingGifs();
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useTrendingImagesQuery = () => {
  return useQuery({
    queryKey: ["trending-images"],
    queryFn: async (): Promise<IGifList> => {
      return await apiSearch.getTrendingImages();
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useCreatorTagsQuery = (id: string) => {
  return useQuery({
    queryKey: ["creators-tags", id],
    queryFn: async () => {
      return await apiMain.fetchCreatorTagsById(id);
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useTagsQuery = () => {
  return useQuery({
    queryKey: ["tags"],
    queryFn: async (): Promise<ITagList> => {
      return await apiTags.getTags();
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useMeGifsQuery = (props: IUserGifsParams) => {
  return useQuery({
    queryKey: ["meGifs", props],
    queryFn: () => apiMain.fetchUserGifs(props),
    ...DEFAULT_OPTIONS,
  });
};

export const useDeleteGifsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const requests = ids.map(id => apiMain.deleteGif(id));
      return await Promise.all(requests);
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: ["meGifs"] });
    },
  });
};

export const useChangeGifsPrivacyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ids, value }: { ids: string[]; value: 0 | 1 }) => {
      const requests = ids.map(id => apiMain.patchGif(id, { published: !!value }));

      return await Promise.all(requests);
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: ["meGifs"] });
    },
  });
};

export const useChangeGifTagsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ids, tags }: { ids: string[]; tags: string[] }) => {
      const requests = ids.map(id => apiMain.patchGif(id, { tags }));
      return await Promise.all(requests);
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: ["meGifs"] });
    },
  });
};

export const useMatchedTagsQuery = (props: IGetMatchedTagsParams) => {
  return useQuery({
    queryKey: ["matchedTags", props],
    queryFn: async () => {
      return await apiTags.getMatchedTags(props);
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useFollowingFeed = () => {
  const fetch = async ({
    pageParam = 1,
  }): Promise<IUserSearchResults> => {
    return await apiMain.getFollowing({
      count: 100,
      page: pageParam as number,
    });
  };

  return useInfiniteQuery({
    queryKey: ["following-feed"],
    queryFn: fetch,
    initialPageParam: 1,
    getNextPageParam: (lastPage: IUserSearchResults) => {
      if (lastPage.page < lastPage.pages) {
        return lastPage.page + 1;
      } else {
        return null;
      }
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useFollowersFeed = () => {
  const fetch = async ({
    pageParam = 1,
  }): Promise<IUserSearchResults> => {
    return await apiMain.getFollowers({
      count: 100,
      page: pageParam,
    });
  };

  return useInfiniteQuery({
    queryKey: ["followers-feed"],
    queryFn: fetch,
    initialPageParam: 1,
    getNextPageParam: (lastPage: IUserSearchResults) => {
      if (lastPage.page < lastPage.pages) {
        return lastPage.page + 1;
      } else {
        return null;
      }
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useDeleteAccountMutation = () => useMutation({
  mutationFn: () => apiMain.deleteMyAccount(),
});

export const useUserCollections = (props: {
  userName: string;
  isRefetch: boolean;
}) => {
  const fetch = async ({
    pageParam = 1,
  }) => {
    return await apiCollections.getUserCollections({
      userId: props.userName,
      page: pageParam as number,
      count: 50,
    });
  };

  return useInfiniteQuery({
    queryKey: ["user-collection", props.isRefetch],
    queryFn: fetch,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.pages) {
        return lastPage.page + 1;
      } else {
        return null;
      }
    },
    ...DEFAULT_OPTIONS,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
};

export const useAddUserCollectionMutation = () => useMutation({
  mutationFn: (props: {
    published: boolean;
    description: string | null;
    folderName: string;
  }) => apiCollections.createCollection(props),
});

export const useUpdateVisitorInfoMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (props: IUpdateParams[]) => apiVisitor.updateVisitorInfo(props),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: ["get-me"] });
    },
  });
};

export const useAddLikeMutation = () => useMutation({
  mutationFn: (props: { gifId: string; sourceId: string | null }) => apiMain.likeGifById(props),
});

export const useRemoveLikeMutation = () => useMutation({
  mutationFn: (props: { gifId: string; sourceId: string | null }) => apiMain.unlikeGifById(props),
});

export const useAddToNiche = () => useMutation({
  mutationFn: (props: { gifId: string; nicheIds: string[] }) => apiNiches.setGifNiches(props),
});

export const useAnalyticsQuery = () => {
  return useQuery<IAnalyticsResponse, IErrorData>({
    queryKey: ["analytics"],
    queryFn: async () => {
      try {
        return await apiMain.getAnalytics();
      } catch (e) {
        throw convertException(e);
      }
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useGifNichesQuery = (id: string) => {
  return useQuery<INicheList>({
    queryKey: ["gif-niche", id],
    queryFn: async () => {
      try {
        return await apiMain.getGifNiches(id);
      } catch (e) {
        throw convertException(e);
      }
    },
    ...DEFAULT_OPTIONS,
    refetchOnMount: true,
  });
};

export const useGifSuggestedNichesQuery = (id: string) => {
  return useQuery<INicheList>({
    queryKey: ["suggested-niche", id],
    queryFn: async () => {
      try {
        return await apiNiches.getGifSuggestedNiche(id);
      } catch (e) {
        throw convertException(e);
      }
    },
    ...DEFAULT_OPTIONS,
    refetchOnMount: true,
  });
};

export const useAddGifSuggestedNichesQuery = (tags: string[]) => {
  return useQuery<INicheList>({
    queryKey: ["upload-suggested-niche", tags],
    queryFn: async () => {
      try {
        return await apiNiches.getAddGifSuggestedNiches(tags);
      } catch (e) {
        throw convertException(e);
      }
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useEditNicheMutation = (nicheId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (props: {
      id: string;
      description: string;
      image: string | null;
      banner: string | null;
      preferences: string[] | null;
      tags: string[] | null;
    }) => apiNiches.editNiche(props),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: [SINGLE_NICHE_KEY, nicheId] });
    },
  });
};

export const useUpdateGifMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, gifParams }: { id: string; gifParams: IPatchGifParams }) => apiMain.patchGif(id, gifParams),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: ["meGifs"] });
    }
  });
};

export const useGetTagLandingFeedQuery = (id: string | undefined) => {
  return useQuery({
    queryKey: ["tag-landing-feed", id],
    queryFn: async (): Promise<ITagLandingFeed | undefined> => {
      if (id) {
        return await apiMain.getTagLandingFeed({ tag: id });
      } else {
        return undefined;
      }
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useAliasLinksQuery = (gifId: string) => {
  return useQuery({
    queryKey: ["alias-links", gifId],
    queryFn: async () => {
      try {
        return await apiMain.getAliasLinks(gifId);
      } catch (e) {
        throw convertException(e);
      }
    },
    ...DEFAULT_OPTIONS,
    gcTime: 0,
  });
};

export const useAddAliasLinkMutation = (gifId: string) => {
  return useMutation({
    mutationFn: () => apiMain.addAliasLink(gifId),
  });
};

export const useGetTagSuggestedQuery = (options: ITagSuggestedOptions) => {
  return useQuery({
    queryKey: ["tags-suggested", options.query],
    queryFn: async () => {
      try {
        return await apiTags.getTagSuggested(options);
      } catch (e) {
        throw convertException(e);
      }
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useGetNichesTagsQuery = ({ niche }: IGetRelatedNicheParams) => {
  return useQuery({
    queryKey: ["niche-tags", niche],
    queryFn: async () => {
      try {
        return await apiNiches.getNichesTags({ niche });
      } catch (e) {
        throw convertException(e);
      }
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useGetNichesCreatorsQuery = ({ niche }: IGetRelatedNicheParams) => {
  return useQuery({
    queryKey: ["niche-creators", niche],
    queryFn: async () => {
      try {
        return await apiNiches.getNichesCreators({ niche });
      } catch (e) {
        throw convertException(e);
      }
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useGetRelatedNichesQuery = ({ niche }: IGetRelatedNicheParams) => {
  return useQuery({
    queryKey: ["related-niches", niche],
    queryFn: async () => {
      try {
        return await apiNiches.getRelatedNiches({ niche });
      } catch (e) {
        throw convertException(e);
      }
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useGetMyNiches = (params: { previews: boolean }) => {
  return useQuery({
    queryKey: ["my-niches"],
    queryFn: async () => {
      try {
        return await apiNiches.getMyNiches(params);
      } catch (e) {
        throw convertException(e);
      }
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useGetTrendingTags = () => {
  return useQuery({
    queryKey: ["trending-searches"],
    queryFn: async () => {
      try {
        return await apiTags.getTrendingTags();
      } catch (e) {
        throw convertException(e);
      }
    },
    ...DEFAULT_OPTIONS,
  });
};

const useGetSearches = <T>(
  keyPrefix: string,
  searchText: string,
  fetchFunction: (searchText: string) => Promise<T>,
  enabled: boolean
) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [`${keyPrefix}/${searchText}`],
    queryFn: async () => {
      const cache = queryClient.getQueryData<T>([`${keyPrefix}/${searchText}`]);

      if (cache) {
        return cache;
      }

      try {
        return await fetchFunction(searchText);
      } catch (e) {
        throw convertException(e);
      }
    },
    ...DEFAULT_OPTIONS,
    enabled,
  });
};

export const useGetCreatorsSearches = (searchText: string) =>
  useGetSearches<ICreatorsItemsList>("creators-searches", searchText, apiSearch.getCreatorsSearches.bind(apiSearch), !!searchText);

export const useGetNichesSearches = (searchText: string) =>
  useGetSearches<INichesItems[]>("niches-searches", searchText, apiSearch.getNichesSearches.bind(apiSearch), !!searchText);

export const useGetTagsSearches = (searchText: string) =>
  useGetSearches<ITagsList[]>("tags-searches", searchText, apiSearch.getTagsSearches.bind(apiSearch), !!searchText);

export const useGetAdSlots = () => {
  return useQuery({
    queryKey: ["ad-slots"],
    queryFn: async () => {
      try {
        return await apiMain.getAdSlots();
      } catch (e) {
        throw convertException(e);
      }
    },
    ...DEFAULT_OPTIONS,
  });
};

export const convertException = (e: unknown): IErrorData => {
  if (e instanceof AxiosError) {
    if (e.response?.data) {
      return e.response.data as IErrorData;
    }

    console.error("OOPS, unknown axios error.", e.response);
  } else {
    console.error("OOPS, unknown exception.", e);
  }

  return {
    error: {
      code: "UnknownError",
      message: "We could not process this request, please try again later.",
      status: 500,
    }
  } as IErrorData;
};
